import React, { useState } from 'react';
import { Button, Container, Icon, Message } from 'semantic-ui-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStockInconsistencySummary } from '../context/StockInconsistencySummaryContext';

const DISMISS_KEY = 'stocky.stockInconsistencyBannerDismissedAt';

function formatRelative(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const diffMs = date.getTime() - Date.now();
  const minutes = Math.round(diffMs / (1000 * 60));
  const hours = Math.round(diffMs / (1000 * 60 * 60));
  const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
  const formatter = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });
  if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute');
  if (Math.abs(hours) < 24) return formatter.format(hours, 'hour');
  return formatter.format(days, 'day');
}

function StockInconsistencyBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getUser, userIsAuthenticated } = useAuth();
  const { summary, loading } = useStockInconsistencySummary();
  const currentUser = getUser();
  const [dismissedAt, setDismissedAt] = useState(() => {
    const raw = sessionStorage.getItem(DISMISS_KEY);
    return raw ? new Date(raw) : null;
  });

  if (!currentUser || !userIsAuthenticated()) return null;
  if (!summary || Number(summary.totalOpen || 0) <= 0) return null;

  const isDifferencesRoute = location.pathname === '/stock-inconsistencies';
  if (isDifferencesRoute) {
    return (
      <div style={{ background: '#FAF5FF', borderBottom: '1px solid #E9D5FF' }}>
        <Container style={{ padding: '8px 0' }}>
          <div style={{ color: '#6D28D9', fontSize: 13 }}>
            {'Resumen r\u00e1pido: '}
            {Number(summary.totalOpen || 0)}
            {' problemas abiertos \u00b7 '}
            {Number(summary.critical || 0)}
            {' cr\u00edticos \u00b7 \u00faltima validaci\u00f3n: '}
            {formatRelative(summary.lastValidationAt)}
          </div>
        </Container>
      </div>
    );
  }

  const latestDetectedAt = summary.latestDetectedAt ? new Date(summary.latestDetectedAt) : null;
  const hasNewIssuesSinceDismiss = latestDetectedAt && dismissedAt ? latestDetectedAt.getTime() > dismissedAt.getTime() : false;
  if (dismissedAt && !hasNewIssuesSinceDismiss) return null;

  const totalOpen = Number(summary.totalOpen || 0);
  const critical = Number(summary.critical || 0);
  const unresolvedListings = Number(summary.unresolvedListings || 0);

  return (
    <div style={{ background: '#FAF5FF', borderBottom: '1px solid #E9D5FF' }}>
      <Container style={{ padding: '12px 0' }}>
        <Message icon={false} style={{ margin: 0, border: '1px solid #C4B5FD', background: '#FFFFFF', boxShadow: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Icon name="warning circle" color={critical > 0 ? 'red' : 'violet'} size="large" />
              <div>
                <div style={{ fontWeight: 700, color: '#5B21B6' }}>
                  {'Detectamos problemas de stock que necesitan revisi\u00f3n.'}
                </div>
                <div style={{ marginTop: 2, color: '#6D28D9' }}>
                  {totalOpen}
                  {' problemas abiertos \u00b7 '}
                  {critical}
                  {' cr\u00edticos \u00b7 '}
                  {unresolvedListings}
                  {' publicaciones sin resolver'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button color="violet" loading={loading} onClick={() => navigate('/stock-inconsistencies')} content="Resolver ahora" />
              <Button
                basic
                color="grey"
                icon="close"
                content="Ocultar"
                onClick={() => {
                  const now = new Date();
                  sessionStorage.setItem(DISMISS_KEY, now.toISOString());
                  setDismissedAt(now);
                }}
              />
            </div>
          </div>
        </Message>
      </Container>
    </div>
  );
}

export default StockInconsistencyBanner;
